import {
  PhArrowLeft,
  PhArrowSquareOut,
  PhArrowsClockwise,
  PhCalendarBlank,
  PhCaretDown,
  PhCaretRight,
  PhCaretUp,
  PhChartBar,
  PhCheck,
  PhCheckCircle,
  PhClipboardText,
  PhCode,
  PhClockCounterClockwise,
  PhCopy,
  PhCreditCard,
  PhCurrencyDollar,
  PhDesktop,
  PhDeviceMobile,
  PhDotsThree,
  PhEnvelopeSimple,
  PhEye,
  PhEyeSlash,
  PhFunnel,
  PhGauge,
  PhKey,
  PhLockKey,
  PhLockSimple,
  PhMoon,
  PhPulse,
  PhPuzzlePiece,
  PhQrCode,
  PhReceipt,
  PhSealCheck,
  PhShieldCheck,
  PhSidebarSimple,
  PhSignOut,
  PhSquaresFour,
  PhStack,
  PhStorefront,
  PhSun,
  PhTag,
  PhTranslate,
  PhUserCircle,
  PhUserGear,
  PhUsersThree,
  PhWarning,
  PhWarningCircle,
  PhX,
  PhXCircle,
} from '@phosphor-icons/vue'
import type { Component } from 'vue'

/**
 * The closed icon registry. Every icon the panel renders is a key here.
 *
 * ## Why a registry and not direct imports at call sites
 *
 * `BaseIcon` is the only renderer, and it reads this map — so an icon's size, weight and
 * colour behaviour are decided once, and a template asks for `name="warning"` rather than
 * importing a component and styling it itself. Twenty call sites each importing and sizing
 * their own icon never look like one product.
 *
 * ## Tree-shaking, and the two things that defeat it
 *
 * `@phosphor-icons/vue` ships ESM with `sideEffects: false`, so **named imports** like the
 * ones above pull in only the icons listed. Two shapes silently pull in all ~1,500:
 * registering the plugin globally (`app.use(PhosphorIcons)`), and importing from the
 * `/compact` entry. Neither appears in this repository, and this comment is so neither
 * is added.
 *
 * Keys are Phosphor's own identity in kebab-case (`PhLockSimple` → `lock-simple`), never a
 * meaning invented here ("danger", "menu"): a key that names what the glyph *is* survives
 * the day someone wants the same glyph for a second meaning.
 */
export const ICONS = {
  'arrow-left': PhArrowLeft,
  'arrow-square-out': PhArrowSquareOut,
  'arrows-clockwise': PhArrowsClockwise,
  'calendar-blank': PhCalendarBlank,
  'caret-down': PhCaretDown,
  'caret-right': PhCaretRight,
  'caret-up': PhCaretUp,
  'chart-bar': PhChartBar,
  check: PhCheck,
  'check-circle': PhCheckCircle,
  'clipboard-text': PhClipboardText,
  code: PhCode,
  'clock-counter-clockwise': PhClockCounterClockwise,
  copy: PhCopy,
  'credit-card': PhCreditCard,
  'currency-dollar': PhCurrencyDollar,
  desktop: PhDesktop,
  'device-mobile': PhDeviceMobile,
  'dots-three': PhDotsThree,
  'envelope-simple': PhEnvelopeSimple,
  eye: PhEye,
  'eye-slash': PhEyeSlash,
  funnel: PhFunnel,
  gauge: PhGauge,
  key: PhKey,
  'lock-key': PhLockKey,
  'lock-simple': PhLockSimple,
  moon: PhMoon,
  pulse: PhPulse,
  'puzzle-piece': PhPuzzlePiece,
  'qr-code': PhQrCode,
  receipt: PhReceipt,
  'seal-check': PhSealCheck,
  'shield-check': PhShieldCheck,
  'sidebar-simple': PhSidebarSimple,
  'sign-out': PhSignOut,
  'squares-four': PhSquaresFour,
  stack: PhStack,
  storefront: PhStorefront,
  sun: PhSun,
  tag: PhTag,
  translate: PhTranslate,
  'user-circle': PhUserCircle,
  'user-gear': PhUserGear,
  'users-three': PhUsersThree,
  warning: PhWarning,
  'warning-circle': PhWarningCircle,
  x: PhX,
  'x-circle': PhXCircle,
} satisfies Record<string, Component>

export type IconName = keyof typeof ICONS

/** Phosphor's weights. `regular` for chrome and controls, `fill` for status glyphs. */
export type IconWeight = 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone'
